import TranslateIcon from "@mui/icons-material/Translate";
import TvIcon from "@mui/icons-material/Tv";
import {
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import axios from "axios";
import dayjs from "dayjs";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_IMAGES } from "../../../../assets";
import AppLoader from "../../../../components/AppLoader";
import CustomDropDown from "../../../../components/CutomDropDown";
import DialogBody from "../../../../components/Dialog/Body";
import DialogActionButtons from "../../../../components/Dialog/Footer";
import DialogHeader from "../../../../components/Dialog/Header";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import UploadBox from "../../../../components/UploadBox";
import {
  PROMOTIONAL_IMAGE_PAGE,
  PROMOTIONAL_IMAGE_SECTION,
} from "../../../../constants/Variables";
import globalStyle from "../../../../globalStyle";
import { setPromotionalBlockCurrentPage } from "../../../../lib/slices/pagination/promotionalBlockPageSlice";
import { baseUrl } from "../../../../services";
import {
  createPromotionalBlock,
  updatePromotionalBlock,
} from "../../../../services/promotionalBlockApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../../../utils";
import { translateText } from "../../../../utils/translationService";
import { addEditPromotionalBlockValidation } from "../../../../utils/Validation";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(4, 2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

// const IMAGE_DIMENSIONS = {
//     desktop: {
//         minWidth: 1870,
//         minHeight: 650,
//         ratio: 1870 / 650,
//     },
//     mobile: {
//         minWidth: 325,
//         minHeight: 501,
//         ratio: 325 / 501,
//     },
// };

let paramsData = {};
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
];

const initialInputValues = {
  image_en: null,
  image_ro: null,
  title_en: "",
  title_ro: "",
  link_en: "",
  link_ro: "",
  page: "",
  positionType: "",
  startDate: null,
  endDate: null,
};

export const AddEditPromotionalBlockDialog = forwardRef(({}, ref) => {
  // Dispatch Hook
  const dispatch = useDispatch();
  // Open Image File En Ref
  const openImageFileEn = useRef(null);
  // Open Image File Ro Ref
  const openImageFileRo = useRef(null);
  // Translation Hook
  const { t } = useTranslation(["catalogue", "common"]);
  // Open State
  const [open, setOpen] = useState(false);
  // Loading State
  const [isLoading, setIsLoading] = useState({
    save: false,
    translateToEnglish: false,
    image_en: false,
    image_ro: false,
  });
  // Input Error State
  const [inputError, setInputError] = useState({});
  // Input Values State
  const [inputValues, setInputValues] = useState(initialInputValues);

  // Get Search Keys State
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  // Get Current Language State
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage,
  );
  // Get Pagination State
  const pagination = useSelector(
    (state) => state.promotionalBlockPaginationReducer,
  );

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          image_en: data?.image?.en || null,
          image_ro: data?.image?.ro || null,
          title_en: capitalizeText(data?.title?.en) || "",
          title_ro: capitalizeText(data?.title?.ro) || "",
          link_en: data?.link?.en || "",
          link_ro: data?.link?.ro || "",
          page: data?.page || "",
          positionType: data?.positionType || "",
          startDate: data?.startDate ? dayjs(data?.startDate) : null,
          endDate: data?.endDate ? dayjs(data?.endDate) : null,
        });
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
  };

  const handleAddEdit = () => {
    const valid = addEditPromotionalBlockValidation(inputValues, setInputError);

    if (valid) {
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    let payload = {
      image: {
        en: inputValues.image_en,
        ro: inputValues.image_ro,
      },
      title: {
        en: inputValues.title_en,
        ro: inputValues.title_ro,
      },
      link: {
        en: inputValues.link_en,
        ro: inputValues.link_ro,
      },
      page: inputValues.page,
      positionType: inputValues.positionType,
      startDate: inputValues.startDate,
      endDate: inputValues.endDate,
    };

    if (paramsData?.type === "add") {
      handleApiCall(createPromotionalBlock, payload, "");
    } else {
      handleApiCall(updatePromotionalBlock, payload, paramsData?.data?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        if (paramsData?.type === "add") {
          dispatch(setPromotionalBlockCurrentPage(1));
        }
        paramsData?.getData(
          searchKeys.promotionalBlockName,
          pagination.page,
          pagination.limit,
          pagination.startDate,
          pagination.endDate,
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.somethingWentWrong", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  const handleChageInputs = (e) => {
    const { name, value, valueAsNumber, type } = e.target;
    const formatedValue = type === "number" ? valueAsNumber : value;

    if (name === "page") {
      setInputValues((pre) => ({ ...pre, positionType: "" }));
    }

    setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
    setInputError((pre) => ({ ...pre, [name]: "" }));
  };

  const handleDateChange = (date, name) => {
    setInputValues((pre) => ({ ...pre, [name]: date }));
    setInputError((pre) => ({ ...pre, [name]: "" }));
  };

  const handleUploadImage = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      handleOpenSnackBar("error", "Only WEBP and GIF formats are allowed");
      event.target.value = null;
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      handleOpenSnackBar("error", "Image size must be 5MB or less");
      event.target.value = null;
      return;
    }

    try {
      setIsLoading((prev) => ({ ...prev, [type]: true }));

      const formData = new FormData();
      formData.append("image", file);

      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setInputError((prev) => ({ ...prev, [type]: false }));
      setInputValues((prev) => ({
        ...prev,
        [type]: response?.data?.url,
      }));

      handleOpenSnackBar(
        "success",
        t("success.mediaUploadSuccess", { ns: "common" }),
      );
    } catch (error) {
      handleOpenSnackBar("error", t("error.mediaUploadFail", { ns: "common" }));
    } finally {
      // reset file inputs
      if (openImageFileEn.current) openImageFileEn.current.value = null;
      if (openImageFileRo.current) openImageFileRo.current.value = null;

      setIsLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const PROMOTIONAL_IMAGE_SECTION_BY_PAGE = useMemo(() => {
    return PROMOTIONAL_IMAGE_SECTION.filter(
      (item) => item.page === inputValues.page,
    );
  }, [inputValues.page]);

  return (
    <>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        maxWidth="md"
      >
        <DialogHeader
          title={
            paramsData.type === "add"
              ? t("addPromotionalImage")
              : t("editPromotionalImage")
          }
          secondaryHeading={
            paramsData.type === "add"
              ? t("addPromotionalImageInstruction")
              : t("editPromotionalImageInstruction")
          }
          icon={<TvIcon />}
          onClose={handleClose}
        />

        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <UploadBox
                inputRef={openImageFileEn}
                label={t("promotionalBlockImageEn")}
                // note={t("promotionalBlockImageEnSizeNote")}
                value={inputValues.image_en}
                error={inputError.image_en}
                loading={isLoading.image_en}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg, image/jpg, image/webp, image/gif"
                height={{ xs: 200, md: 130 }}
                previewSizeProps={{
                  width: { xs: "100%", md: "100%" },
                  height: { xs: "auto", md: "auto" },
                  p: 0,
                  overflow: "hidden",
                }}
                imageProps={{
                  width: "100%",
                  height: "100%",
                  sx: { objectFit: "fill" },
                }}
                onClick={() => openImageFileEn.current.click()}
                onChange={(event) => handleUploadImage(event, "image_en")}
                onRemove={() =>
                  setInputValues({ ...inputValues, image_en: null })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <UploadBox
                inputRef={openImageFileRo}
                label={t("promotionalBlockImageRo")}
                // note={t("promotionalBlockImageRoSizeNote")}
                value={inputValues.image_ro}
                error={inputError.image_ro}
                loading={isLoading.image_ro}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg, image/jpg, image/webp, image/gif"
                height={{ xs: 200, md: 130 }}
                previewSizeProps={{
                  width: { xs: "100%", md: "100%" },
                  height: { xs: "auto", md: "auto" },
                  p: 0,
                  overflow: "hidden",
                }}
                imageProps={{
                  width: "100%",
                  height: "100%",
                  sx: { objectFit: "fill" },
                }}
                onClick={() => openImageFileRo.current.click()}
                onChange={(event) => handleUploadImage(event, "image_ro")}
                onRemove={() =>
                  setInputValues({ ...inputValues, image_ro: null })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("titleInRomanian")}
                htmlFor={`promotional-block-title-ro`}
              />
              <TextInput
                id={`promotional-block-title-ro`}
                required
                placeholder={t("enterTitleInRomanian")}
                name={`title_ro`}
                textLength={60}
                value={inputValues.title_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.title_ro)}
                helperText={t(inputError.title_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.title_ro?.length}/60
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("titleInEnglish")}
                  htmlFor={`promotional-block-title-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("title_ro", "title_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`promotional-block-title-en`}
                required
                placeholder={t("enterTitleInEnglish")}
                name={`title_en`}
                textLength={60}
                value={inputValues.title_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.title_en)}
                helperText={t(inputError.title_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_en?.length}/60
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label
                optional
                labelText={t("linkInRomanian")}
                htmlFor={`promotional-block-link-ro`}
              />
              <TextInput
                id={`promotional-block-link-ro`}
                required
                placeholder={t("enterLinkInRomanian")}
                name={`link_ro`}
                textLength={60}
                value={inputValues.link_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.link_ro)}
                helperText={t(inputError.link_ro)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                optional
                labelText={t("linkInEnglish")}
                htmlFor={`promotional-block-link-en`}
              />
              <TextInput
                id={`promotional-block-link-en`}
                required
                placeholder={t("enterLinkInEnglish")}
                name={`link_en`}
                textLength={60}
                value={inputValues.link_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.link_en)}
                helperText={t(inputError.link_en)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("page")}
                htmlFor={"promotional-block-page"}
              />
              <CustomDropDown
                id={"promotional-block-page"}
                name={"page"}
                value={inputValues.page}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.page)}
                errorText={t(inputError.page)}
              >
                <MenuItem value="" disabled>
                  {t("selectPage")}
                </MenuItem>
                {PROMOTIONAL_IMAGE_PAGE.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label[currentLanguage]}
                  </MenuItem>
                ))}
              </CustomDropDown>
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("positionType")}
                htmlFor={"promotional-block-position-type"}
              />
              <CustomDropDown
                id={"promotional-block-position-type"}
                name={"positionType"}
                value={inputValues.positionType}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.positionType)}
                errorText={t(inputError.positionType)}
                disabled={!inputValues.page}
              >
                <MenuItem value="" disabled>
                  {t("selectPositionType")}
                </MenuItem>
                {PROMOTIONAL_IMAGE_SECTION_BY_PAGE.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label[currentLanguage]}
                  </MenuItem>
                ))}
              </CustomDropDown>
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("startDate")}
                htmlFor={"start-date"}
              />
              <DatePicker
                id={"start-date"}
                sx={{ width: "100%", ...globalStyle.textField }}
                name={"startDate"}
                value={inputValues.startDate}
                onChange={(date) => handleDateChange(date, "startDate")}
                slotProps={{
                  textField: {
                    error: inputError.startDate,
                    helperText: t(inputError.startDate),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label required labelText={t("endDate")} htmlFor={"end-date"} />
              <DatePicker
                id={"end-date"}
                sx={{ width: "100%", ...globalStyle.textField }}
                minDate={inputValues.startDate}
                name={"endDate"}
                value={inputValues.endDate}
                onChange={(date) => handleDateChange(date, "endDate")}
                slotProps={{
                  textField: {
                    error: inputError.endDate,
                    helperText: t(inputError.endDate),
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEdit}
          onCancel={handleClose}
          confirmText={"save"}
          cancelText={"cancel"}
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
    </>
  );
});
